import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2, RotateCcw, Eye, EyeOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface TeamActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface UserProfile {
  userId: string;
  role?: string;
  hiddenFromActivity?: boolean;
}

interface UserWithActivity {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  profile?: UserProfile;
}

export function TeamActivityDialog({ open, onOpenChange }: TeamActivityDialogProps) {
  const { toast } = useToast();
  const [confirmResetUserId, setConfirmResetUserId] = useState<string | null>(null);

  const { data: users = [], isLoading } = useQuery<UserWithActivity[]>({
    queryKey: ["/api/users-with-profiles"],
    queryFn: async () => {
      const response = await fetch("/api/users-with-profiles");
      if (!response.ok) throw new Error("Failed to fetch users");
      return response.json();
    },
    enabled: open,
  });

  const toggleVisibilityMutation = useMutation({
    mutationFn: async ({ userId, hidden }: { userId: string; hidden: boolean }) => {
      const response = await apiRequest("PATCH", `/api/admin/users/${userId}/activity-visibility`, { hidden });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users-with-profiles"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user-activity"] });
      toast({ title: "Updated", description: "User visibility has been updated." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update visibility.", variant: "destructive" });
    },
  });

  const resetActivityMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiRequest("POST", `/api/admin/users/${userId}/reset-activity`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user-activity"] });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      setConfirmResetUserId(null);
      toast({ title: "Activity Reset", description: "User's activity history has been reset." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to reset activity.", variant: "destructive" });
    },
  });

  const getUserDisplayName = (user: UserWithActivity) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ""} ${user.lastName || ""}`.trim();
    }
    return user.email || user.id;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-full sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Team Activity Management</DialogTitle>
            <DialogDescription>
              Control which users appear in the Team Activity panel and reset activity history
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No users found.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-center">Visible</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{getUserDisplayName(user)}</div>
                        {user.email && (
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {user.profile?.role?.replace("_", " ") || "Unknown"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        {user.profile?.hiddenFromActivity ? (
                          <EyeOff className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <Eye className="w-4 h-4 text-green-500" />
                        )}
                        <Switch
                          checked={!user.profile?.hiddenFromActivity}
                          onCheckedChange={(checked) => {
                            toggleVisibilityMutation.mutate({
                              userId: user.id,
                              hidden: !checked,
                            });
                          }}
                          disabled={toggleVisibilityMutation.isPending}
                          data-testid={`switch-visibility-${user.id}`}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmResetUserId(user.id)}
                        data-testid={`button-reset-${user.id}`}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Reset
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmResetUserId} onOpenChange={(open) => !open && setConfirmResetUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset User Activity?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all activity logs (calls, emails, notes) created by this user. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-reset">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmResetUserId && resetActivityMutation.mutate(confirmResetUserId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={resetActivityMutation.isPending}
              data-testid="button-confirm-reset"
            >
              {resetActivityMutation.isPending ? "Resetting..." : "Reset Activity"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
